import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useHouseholdId } from '@/lib/household';
import { worker } from '@/lib/receipts';

/** No persistent photo cache. Only visible images fetch; duplicate ribbon/grid
 * requests share one in-flight query. Session changes clear the app QueryClient. */
export default function JournalPhoto({ id, className = '', natural, original = false }: {
  id: string; className?: string; natural?: boolean; original?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(entries => {
      setVisible(entries.some(e => e.isIntersecting));
    }, { rootMargin: '120px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className={`journal-photo ${className} ${natural ? 'aspect-[4/5]' : ''}`}>
    {visible ? <VisiblePhoto key={`${id}-${original}`} id={id} original={original}/> : <span className="journal-photo-placeholder" aria-hidden="true">✿</span>}
  </div>;
}

function VisiblePhoto({id,original}:{id:string;original:boolean}) {
  const uid = useAuth().session?.user.id; const hid = useHouseholdId();
  const variant = original ? 'original' : 'thumb';
  const [broken,setBroken] = useState<Blob|null>(null);
  const photo = useQuery({
    queryKey: ['journal-image', hid, uid, id, variant],
    enabled: !!hid && !!uid,
    staleTime: 0, gcTime: 0, retry: false, refetchOnMount: false, refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      const response = await worker(`/journal/${id}/file${variant === 'thumb' ? '?variant=thumb' : ''}`, { signal });
      if (!response.ok) throw new Error('Photo unavailable');
      return response.blob();
    },
  });
  const [image, setImage] = useState<{ blob: Blob; url: string } | null>(null);
  useEffect(() => {
    if (!photo.data) return;
    const url = URL.createObjectURL(photo.data); setImage({ blob: photo.data, url });
    return () => { URL.revokeObjectURL(url); setImage(null); };
  }, [photo.data]);
  const failed = photo.isError || (!!photo.data && broken === photo.data);
  const src = !failed && image?.blob === photo.data ? image?.url : undefined;
  return src ? <img src={src} alt="" decoding="async" className="journal-photo-image" onError={() => setBroken(photo.data ?? null)}/> : <span className="journal-photo-placeholder">{failed ? 'Photo unavailable' : <span aria-hidden="true">✿</span>}</span>;
}
